import {
  Component,
  ChangeDetectorRef,
  Input,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { QaiTableComponent, TableRow, QaiLoadingComponent, SortDirection } from '@qai/ui';
import { finalize, tap } from 'rxjs';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { AssignmentsService } from 'src/app/services/self-serve/assignments.service';
import { ISimulation } from 'src/app/interfaces/self-serve/responses/isimulation';
import { IAssignment } from 'src/app/interfaces/self-serve/assignment-manager/iassignment';
import { CallTypeLabelMap } from 'src/app/utils/constants/self-serve/simulation.constants';
import { formatLongMonthDate } from 'src/app/utils/date-parser.util';

const SIMULATION_TABLE_HEADERS = [
  'Name',
  'Product',
  'Published date',
  'Used in',
  'Simulation type',
];

@Component({
  selector: 'published-simulations-table',
  standalone: true,
  templateUrl: './published-simulations-table.component.html',
  styleUrls: ['./published-simulations-table.component.scss'],
  imports: [CommonModule, QaiTableComponent, QaiLoadingComponent],
})
export class PublishedSimulationsTableComponent {
  tableData: TableRow[] = [];
  readonly headers = SIMULATION_TABLE_HEADERS;
  isLoading = true;
  initialSortColumn: string = SIMULATION_TABLE_HEADERS[2];
  initialSortDirection: SortDirection = 'desc';
  isTooltipLoading = false;
  private simulations: ISimulation[] = [];
  @ViewChild(QaiTableComponent)
  qaiTableRef: QaiTableComponent | null = null;
  @Input() selectedAccount: { label: string; value: string } | undefined;
  @Input() selectedAccountUuid: string = '';

  constructor(
    private authorService: AuthorService,
    private assignmentsService: AssignmentsService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedAccount']) {
      this.loadSimulations();
    }
  }

  private loadSimulations() {
    const accountUuid = this.selectedAccount?.value || this.selectedAccountUuid;

    if (!accountUuid) {
      console.error('No valid account UUID found to load simulations.');
      this.isLoading = false;
      return;
    }

    this.authorService
      .findAllSimulations(accountUuid, 'PUBLISHED')
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdRef.detectChanges();
        })
      )
      .subscribe({
        next: (simulations) => {
          this.simulations = simulations;
          this.tableData = this.transformApiDataToTableRows(simulations);
        },
        error: (err: any) => {
          console.error('Error loading simulations:', err);
          this.isLoading = false;
        },
      });
  }

  private transformApiDataToTableRows(apiData: ISimulation[]): TableRow[] {
    return apiData
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )
      .map((simulation) =>
        this.createSimulationTableRow(
          simulation.uuid,
          simulation.name,
          simulation.moduleName,
          formatLongMonthDate(simulation.publishedAt),
          this.formatAssignmentCount(simulation.assignmentCount),
          this.getCallTypeLabel(simulation.callType)
        )
      );
  }

   onTooltipDataRequested = ({ row }: { row: { id: string | number } }) => {
    const simulationId = String(row.id);
    const simulation = this.simulations.find((sim) => sim.uuid === simulationId);
    const moduleUuid = simulation?.moduleUuid || '';

    this.isTooltipLoading = true;

    this.assignmentsService.findAssignmentsForSimulation(simulationId, moduleUuid)
      .pipe(
        tap((res) =>
          console.log(`Fetched all assignments for simulation ${simulationId}`, res)
        ),
        finalize(() => {
          this.isTooltipLoading = false;
          this.cdRef.detectChanges();
        })
      )
      .subscribe({
        next: (assignments: IAssignment[]) => {
          const assignmentNames = assignments.map((a) => a.name || 'Untitled');
          this.qaiTableRef?.setTooltipData(assignmentNames);
        },
        error: (err: any) => {
          console.error(`Failed to fetch assignments for simulation ${simulationId}:`, err);
          this.qaiTableRef?.setTooltipData(['Error loading assignments']);
        },
      });
  };


  private getCallTypeLabel(callType?: string): string {
    if (!callType) return 'Unknown';

    const key = callType.toLowerCase() as keyof typeof CallTypeLabelMap;
    return CallTypeLabelMap[key] || this.capitalizeFirstLetter(callType);
  }

  private capitalizeFirstLetter(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  createSimulationTableRow(
    id: string | number,
    name: string,
    moduleName: string,
    date: string,
    usage: string,
    simulationType: string
  ): TableRow {
    const row = {
      id,
      [SIMULATION_TABLE_HEADERS[0]]: { type: 'text', value: name },
      [SIMULATION_TABLE_HEADERS[1]]: { type: 'text', value: moduleName },
      [SIMULATION_TABLE_HEADERS[2]]: { type: 'text', value: date },
      [SIMULATION_TABLE_HEADERS[3]]: { type: 'tooltip', value: [], label: usage},
      [SIMULATION_TABLE_HEADERS[4]]: {
        type: 'badge',
        shape: 'rectangle',
        kind: 'gray',
        label: simulationType,
      },
    } as const;
    return { ...row } as unknown as TableRow;
  }

  private formatAssignmentCount(assignmentCount: number): string {
    if (!assignmentCount || assignmentCount === 0) {
      return 'None';
    }

    return `${assignmentCount} assignment${assignmentCount > 1 ? 's' : ''}`;
  }
}
